/* GENERADO — no editar a mano.
 *
 * Derivado de flow/src/components/shared/blocks/highlight.tsx quitandole los tipos, para
 * que la previsualizacion del back office editorial monte los MISMOS
 * componentes que renderiza /historias/[slug] en el sitio publico.
 *
 * Se regenera con:                 npm run historia:sync
 * Se verifica (falla si difiere):   npm run historia:diff
 *
 * Un cambio en el componente de flow se trae con `historia:sync`. Editar este
 * archivo directamente lo pierde en la siguiente regeneracion — y peor: hace
 * que la previsualizacion muestre algo distinto de lo que se publica, que es
 * justo lo que esa pantalla existe para evitar.
 *
 * La salida definitiva es publicar estos componentes en el Design System y que
 * los dos repos los consuman; ver
 * docs/superpowers/specs/2026-08-26-editorial-design-system-design.md §5.
 */
/* Resaltado FancyYool dentro de un texto.
 *
 * POR QUE EXISTE. Los cuatro bloques que resaltan una parte de su titular
 * (FeaturedStory, EditorialSplit, ProductBand, SegmentSwitcher) tenian la misma
 * funcion copiada, y esa funcion buscaba UNA subcadena con `indexOf`. Para
 * resaltar dos palabras sueltas de una frase —"Simulamos" y "contratamos" en
 * "Simulamos y contratamos el mismo dia"— el editor no tenia forma: escribir
 * "Simulamos contratamos" busca esa cadena literal, que no existe en el texto,
 * asi que no se resaltaba nada.
 *
 * Es la misma logica que ya usa la configuracion de planes y productos
 * (tadmin/src/components/tabs/ConfiguracionPlanTab.jsx): alternancia de regex
 * con los terminos escapados, del mas largo al mas corto.
 *
 * DOS DIFERENCIAS con lo que hacia antes, ambas deliberadas y heredadas de esa
 * implementacion: no distingue mayusculas, y resalta TODAS las apariciones del
 * termino, no solo la primera.
 */
import React from 'react';

/* FancyYool: color de texto, peso 700. Las variantes `dark-*` son para fondos
   navy, foto u oscuros. Repetido en cada bloque porque son espejos del design
   system; aca vive la copia que usa el resaltado. */

const FANCY = {
  cyan: 'var(--fy-cyan)',
  blue: 'var(--fy-blue)',
  sky: 'var(--fy-sky)',
  violet: 'var(--fy-violet)',
  indigo: 'var(--fy-indigo)',
  purple: 'var(--fy-purple)',
  pink: 'var(--fy-pink)',
  red: 'var(--fy-red)',
  orange: 'var(--fy-orange)',
  lime: 'var(--fy-lime)',
  teal: 'var(--fy-teal)',
  slate: 'var(--fy-slate)',
  'dark-teal': 'var(--fy-dark-teal)',
  'dark-cyan': 'var(--fy-dark-cyan)',
  'dark-indigo': 'var(--fy-dark-indigo)',
  'dark-yellow': 'var(--fy-dark-yellow)'
};

/** Un termino a resaltar. Sin `color` propio, toma el del bloque. */

/**
 * Lo que llega desde la base.
 *
 * El `string` suelto es el formato viejo —una sola frase— y se sigue aceptando:
 * hay contenido publicado guardado asi, y los bloques se usan tambien fuera de
 * las historias, con la prop escrita a mano en el codigo de la pagina.
 */

/** Escapa lo que en una regex significaria otra cosa. */
const escapar = valor => valor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Normaliza a una lista de terminos utiles, del mas largo al mas corto.
 *
 * El orden importa: con "contratamos" y "contratamos el mismo dia" en la lista,
 * la alternancia elige la primera que calza. Si va antes la corta, la larga
 * nunca se usa y el resaltado queda partido a la mitad.
 */
export function highlightTerms(highlight) {
  if (!highlight) return [];
  const lista = typeof highlight === 'string' ? [{
    texto: highlight
  }] : highlight;
  if (!Array.isArray(lista)) return [];
  return lista.filter(t => Boolean(t) && typeof t?.texto === 'string' && t.texto.trim().length > 0).map(t => ({
    texto: t.texto.trim(),
    color: t.color
  })).sort((a, b) => b.texto.length - a.texto.length);
}

/** El color del termino; si no trae uno valido, el del bloque. */
function tono(color, porDefecto) {
  // `color` viene de un jsonb, asi que puede ser cualquier cosa. Un token
  // desconocido cae al color del bloque en vez de dejar el texto sin color.
  if (color && FANCY[color]) return FANCY[color];
  return FANCY[porDefecto];
}

/**
 * Devuelve `texto` con cada termino envuelto en su color.
 *
 * Sin terminos utiles devuelve el texto tal cual — no un fragmento vacio, para
 * que el bloque pueda seguir tratandolo como cadena.
 */
export function renderHighlighted(texto, highlight, porDefecto) {
  const terminos = highlightTerms(highlight);
  if (!texto || terminos.length === 0) return texto;
  const patron = terminos.map(t => escapar(t.texto)).join('|');
  // El grupo de captura es lo que hace que `split` devuelva tambien los trozos
  // que calzaron, intercalados con los que no.
  const partes = texto.split(new RegExp(`(${patron})`, 'gi'));
  return <>
      {partes.map((parte, i) => {
      const termino = terminos.find(t => t.texto.toLowerCase() === parte.toLowerCase());
      if (!termino) return <React.Fragment key={i}>{parte}</React.Fragment>;
      return <span key={i} style={{
        color: tono(termino.color, porDefecto),
        fontWeight: 700
      }}>
            {parte}
          </span>;
    })}
    </>;
}
